import React, { useMemo } from "react";

const VocabularyCard = React.memo(({
  word,
  searchQuery,
  hiddenVietnameseMeanings,
  getReviewPriority,
  getPriorityColor,
  getPriorityIcon,
  getStepDescription,
  highlightSearchText,
  playVocabAudio,
  playSentenceAudio,
  toggleVietnameseMeaning,
  editVocabulary,
  deleteVocabulary
}) => {
  // Memoize expensive calculations to prevent recalculation on every render
  const memoizedData = useMemo(() => {
    const priority = getReviewPriority(word);
    const priorityColor = getPriorityColor(priority);
    const priorityIcon = getPriorityIcon(priority);
    const stepDescription = getStepDescription(word.step);
    const accuracy = word.reviewCount > 0 ? Math.round((word.correctCount / word.reviewCount) * 100) : 0;
    
    const priorityText = priority === "urgent"
      ? "KHẨN CẤP"
      : priority === "warning"
      ? "CẢNH BÁO"
      : priority === "due"
      ? "ĐẾN HẠN"
      : "ỔN ĐỊNH";

    return {
      priority,
      priorityColor,
      priorityIcon,
      stepDescription,
      accuracy,
      priorityText
    };
  }, [word, getReviewPriority, getPriorityColor, getPriorityIcon, getStepDescription]);

  // Memoize date formatting
  const formattedDates = useMemo(() => ({
    createdAt: new Date(word.createdAt).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" }),
    nextReview: new Date(word.nextReview).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" })
  }), [word.createdAt, word.nextReview]);

  // Memoize highlighted text to prevent regex recalculation
  const highlightedText = useMemo(() => ({
    wordJp: highlightSearchText(word.wordJp, searchQuery),
    wordVi: highlightSearchText(word.wordVi, searchQuery),
    exampleSentence: word.exampleSentence ? highlightSearchText(word.exampleSentence, searchQuery) : null
  }), [word.wordJp, word.wordVi, word.exampleSentence, searchQuery, highlightSearchText]);

  return (
    <div className={`bg-black/60 border border-gray-600/50 rounded-xl p-4 backdrop-blur-sm transition-all duration-300 hover:border-cyan-400/50 hover:shadow-neon-cyan ${memoizedData.priorityColor}`}>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
        <div className="flex-1">
          {/* Priority indicator */}
          <div className="flex items-center gap-3 mb-3">
            <span className="text-lg">{memoizedData.priorityIcon}</span>
            <span className="text-xs font-mono font-bold px-2 py-1 rounded-lg bg-black/60 border border-cyan-400/50 text-cyan-300">
              {memoizedData.priorityText}
            </span>
          </div>

          <div className="space-y-2 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0">
            <div className="flex items-center gap-2">
              <strong className="text-xl font-mono font-bold text-cyan-300 break-words">
                {highlightedText.wordJp}
              </strong>
              <button
                onClick={() => playVocabAudio(word.audioVocab, word.wordJp)}
                className="text-pink-400 hover:text-pink-300 transition-colors flex-shrink-0 p-1 rounded-lg hover:bg-pink-400/20"
                title={word.audioVocab ? "PHÁT ÂM THANH" : "GIỌNG ĐỌC TỰ ĐỘNG"}
              >
                {word.audioVocab ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                  </svg>
                )}
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-lg font-mono text-green-300 break-words">
                {hiddenVietnameseMeanings.has(word.id)
                  ? "___________"
                  : highlightedText.wordVi}
              </span>
              <button
                onClick={() => toggleVietnameseMeaning(word.id)}
                className="p-1 bg-black/60 border border-green-400/50 rounded-lg hover:border-green-400 hover:shadow-neon-green transition-all duration-300 text-green-300 hover:text-green-100 transform hover:scale-105 flex-shrink-0"
              >
                {hiddenVietnameseMeanings.has(word.id) ? 
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="size-4">
                <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
                <path fillRule="evenodd" d="M1.38 8.28a.87.87 0 0 1 0-.566 7.003 7.003 0 0 1 13.238.006.87.87 0 0 1 0 .566A7.003 7.003 0 0 1 1.379 8.28ZM11 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" clipRule="evenodd" />
              </svg>
              
              
                 : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="size-4">
                 <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 0 0-1.06 1.06l10.5 10.5a.75.75 0 1 0 1.06-1.06l-1.322-1.323a7.012 7.012 0 0 0 2.16-3.11.87.87 0 0 0 0-.567A7.003 7.003 0 0 0 4.82 3.76l-1.54-1.54Zm3.196 3.195 1.135 1.136A1.502 1.502 0 0 1 9.45 8.389l1.136 1.135a3 3 0 0 0-4.109-4.109Z" clipRule="evenodd" />
                 <path d="m7.812 10.994 1.816 1.816A7.003 7.003 0 0 1 1.38 8.28a.87.87 0 0 1 0-.566 6.985 6.985 0 0 1 1.113-2.039l2.513 2.513a3 3 0 0 0 2.806 2.806Z" />
               </svg>
               }
              </button>
            </div>
          </div>

          {/* Image Display */}
          {word.imageUrl && (
            <div className="mt-4 bg-black/40 border border-orange-400/30 rounded-xl p-3 backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 text-orange-400">
                  <path fillRule="evenodd" d="M2 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4Z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M10.5 5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM12 9.5 9.5 7 6.5 10.5 5 9l-2 2.5h9Z" clipRule="evenodd" />
                </svg>
                <span className="text-xs font-mono font-bold text-orange-300">HÌNH ẢNH MINH HỌA</span>
              </div>
              <div className="flex justify-center">
                <img
                  src={word.imageUrl}
                  alt={`Hình ảnh minh họa cho ${word.wordJp}`}
                  className="max-w-full max-h-32 sm:max-h-40 rounded-lg border border-orange-400/40 object-contain cursor-pointer hover:border-orange-400 transition-all duration-300 hover:shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                  onLoad={(e) => {
                    e.target.style.display = 'block';
                    e.target.nextSibling.style.display = 'none';
                  }}
                  onClick={(e) => {
                    // Open image in new tab when clicked
                    window.open(word.imageUrl, '_blank');
                  }}
                  title="Click to view full size"
                />
                <div 
                  className="flex items-center justify-center text-red-300 text-xs font-mono p-3 border border-red-400/50 rounded-lg bg-black/60"
                  style={{ display: 'none' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-4 h-4 mr-2">
                    <path fillRule="evenodd" d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.3 1.3 0 0 1-1.299 2H2.804a1.3 1.3 0 0 1-1.3-2l5.197-9ZM8 4a.75.75 0 0 1 .75.75v3a.75.75 0 1 1-1.5 0v-3A.75.75 0 0 1 8 4Zm0 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
                  </svg>
                  Failed to load image
                </div>
              </div>
            </div>
          )}

          {word.exampleSentence && (
            <div className="mt-3 text-sm text-gray-300 italic break-words flex items-center font-mono">
              <span className="text-purple-400">{">"}</span>
              <span className="ml-2">
                {highlightedText.exampleSentence}
              </span>
              {word.audioSentence && (
                <button
                  onClick={() => playSentenceAudio(word.audioSentence, word.sentenceStartTime, word.sentenceEndTime)}
                  className="ml-2 text-green-400 hover:text-green-300 transition-colors p-1 rounded-lg hover:bg-green-400/20"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="bg-black/60 border border-cyan-400/30 rounded-lg px-2 py-2 flex flex-row items-center gap-1">
              <span className="font-mono font-bold text-cyan-400">TẠO:</span>
              <div className="text-xs text-cyan-300 font-mono">
                {formattedDates.createdAt}
              </div>
            </div>
            <div className="bg-black/60 border border-pink-400/30 rounded-lg px-2 py-2 flex flex-row items-center gap-1">
              <span className="font-mono font-bold text-pink-400">ÔN TẬP:</span>
              <div className="text-xs text-pink-300 font-mono">{word.reviewCount}x</div>
            </div>
            <div className="bg-black/60 border border-green-400/30 rounded-lg px-2 py-2 flex flex-row items-center gap-1">
              <span className="font-mono font-bold text-green-400">ĐỘ CHÍNH XÁC:</span>
              <div className="text-xs text-green-300 font-mono">
                {memoizedData.accuracy}%
              </div>
            </div>
            <div className="bg-black/60 border border-purple-400/30 rounded-lg px-2 py-2 flex flex-row items-center gap-1">
              <span className="font-mono font-bold text-purple-400">TIẾP THEO:</span>
              <div className="text-xs text-purple-300 font-mono">
                {formattedDates.nextReview}
              </div>
            </div>
          </div>

          {/* Difficulty info */}
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <div className="bg-black/60 border border-yellow-400/30 rounded-lg px-2 py-1 flex items-center gap-1">
              <span className="font-mono font-bold text-yellow-400">CẤP ĐỘ:</span>
              <span className="text-yellow-300 font-mono">{memoizedData.stepDescription}</span>
            </div>
            <div className="bg-black/60 border border-blue-400/30 rounded-lg px-2 py-1 flex items-center gap-1">
              <span className="font-mono font-bold text-blue-400">ĐỘ KHÓ:</span>
              <span className="text-blue-300 font-mono uppercase">{word.difficulty || "trung bình"}</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-row sm:flex-col gap-2 mt-4 sm:mt-0 sm:ml-4">
          <button
            onClick={() => editVocabulary(word)}
            className="p-2 bg-black/60 border border-blue-400/50 text-blue-300 hover:text-blue-100 hover:border-blue-400 rounded-lg transition-all duration-300 hover:shadow-neon-cyan transform hover:scale-105 backdrop-blur-sm"
            title="[EDIT_UNIT]"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => deleteVocabulary(word.id)}
            className="p-2 bg-black/60 border border-red-400/50 text-red-300 hover:text-red-100 hover:border-red-400 rounded-lg transition-all duration-300 hover:shadow-neon-pink transform hover:scale-105 backdrop-blur-sm"
            title="[DELETE_UNIT]"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
});

export default VocabularyCard; 